import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { conferences } from '../api';

const PauseSlide = ({ presentation, conference }) => {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const interval = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="w-full h-full flex flex-col items-center justify-center bg-navy-900 text-white p-12">
      <p className="text-7xl font-mono font-bold mb-8 text-gold-500">
        {time.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
      </p>
      <h2 className="text-4xl font-bold mb-4 text-cream-100">{presentation.title}</h2>
      {presentation.start_time && presentation.end_time && (
        <p className="text-xl text-navy-300 mb-4">{presentation.start_time.slice(0, 5)} - {presentation.end_time.slice(0, 5)}</p>
      )}
      {presentation.description && (
        <p className="text-xl text-navy-200 mb-8 max-w-2xl text-center">{presentation.description}</p>
      )}
      <p className="text-navy-400 mt-12 text-lg tracking-widest uppercase">{conference?.title}</p>
    </div>
  );
};

const InfoSlide = ({ presentation, conference }) => {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const interval = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="w-full h-full flex flex-col items-center justify-center bg-navy-900 text-white p-12">
      <p className="text-5xl font-mono font-bold mb-10 text-gold-500">
        {time.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
      </p>
      <h2 className="text-4xl font-bold mb-6 text-cream-100">{presentation.title}</h2>
      {presentation.description && (
        <p className="text-xl text-navy-200 max-w-2xl text-center">{presentation.description}</p>
      )}
      <p className="text-navy-400 mt-12 text-lg tracking-widest uppercase">{conference?.title}</p>
    </div>
  );
};

const Present = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [conference, setConference] = useState(null);
  const [presentationList, setPresentationList] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState(null);
  const [timerActive, setTimerActive] = useState(false);
  const [showProgram, setShowProgram] = useState(false);

  useEffect(() => {
    conferences.getOne(id)
      .then((res) => { setConference(res.data); setPresentationList(res.data.presentations || []); })
      .catch(() => setError('Kunne ikke hente konferanse'))
      .finally(() => setLoading(false));
  }, [id]);

  const currentPresentation = presentationList[currentIndex];

  const startTimer = useCallback(() => {
    if (!currentPresentation?.start_time || !currentPresentation?.end_time) return;
    const [startH, startM] = currentPresentation.start_time.split(':').map(Number);
    const [endH, endM] = currentPresentation.end_time.split(':').map(Number);
    const totalMinutes = (endH * 60 + endM) - (startH * 60 + startM);
    setTimeLeft(totalMinutes * 60);
    setTimerActive(true);
  }, [currentPresentation]);

  useEffect(() => {
    if (!timerActive || timeLeft === null) return;
    const interval = setInterval(() => {
      setTimeLeft((prev) => { if (prev <= 0) { clearInterval(interval); return 0; } return prev - 1; });
    }, 1000);
    return () => clearInterval(interval);
  }, [timerActive, timeLeft]);

  useEffect(() => { setTimerActive(false); setTimeLeft(null); }, [currentIndex]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        setCurrentIndex((prev) => Math.min(presentationList.length - 1, prev + 1));
      }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        setCurrentIndex((prev) => Math.max(0, prev - 1));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [presentationList.length]);

  const formatTime = (seconds) => {
    const m = Math.floor(Math.abs(seconds) / 60);
    const s = Math.abs(seconds) % 60;
    return `${seconds < 0 ? '-' : ''}${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const getTimerColor = () => {
    if (timeLeft === null) return 'text-navy-400';
    if (timeLeft <= 0) return 'text-red-400';
    if (timeLeft <= (conference?.timer_warning_minutes || 10) * 60) return 'text-gold-400';
    return 'text-green-400';
  };

  const getFileUrl = (presentation) => {
    if (!presentation) return '';
    const fileUrl = `https://podium.botcenter.app/uploads/${id}/${presentation.filename}`;
    if (presentation.file_type === '.pdf') return fileUrl;
    return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`;
  };

  const getProgramIcon = (type) => {
    if (type === 'pause') return '☕';
    if (type === 'info') return 'ℹ️';
    return '🎤';
  };

  if (loading) return <div className="flex items-center justify-center h-screen bg-navy-900"><p className="text-navy-300">Laster...</p></div>;
  if (error) return <div className="flex items-center justify-center h-screen bg-navy-900"><p className="text-red-400">{error}</p></div>;

  return (
    <div className="h-screen flex flex-col bg-navy-950">
      <div className="bg-navy-900 border-b border-navy-800 px-4 py-2 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(`/conference/${id}`)} className="text-navy-400 hover:text-cream-100 text-sm transition-colors">
            ← Avslutt
          </button>
          <span className="text-gold-500 font-bold tracking-wide">PODIUM</span>
          <span className="text-cream-200 text-sm font-medium">{conference?.title}</span>
        </div>

        <div className="flex items-center gap-3">
          {conference?.show_timer && currentPresentation?.type === 'presentation' && (
            <div className={`font-mono text-xl font-bold ${getTimerColor()}`}>
              {timeLeft !== null ? formatTime(timeLeft) : '--:--'}
            </div>
          )}
          {conference?.show_timer && currentPresentation?.start_time && currentPresentation?.type === 'presentation' && (
            <button onClick={startTimer} className="bg-green-700 text-white px-3 py-1 rounded-lg text-sm hover:bg-green-800 transition">
              Start timer
            </button>
          )}
          <button onClick={() => setShowProgram(!showProgram)}
            className="bg-navy-800 text-cream-200 px-3 py-1 rounded-lg text-sm hover:bg-navy-700 transition">
            Program
          </button>
        </div>

        <div className="flex items-center gap-2">
          <button onClick={() => setCurrentIndex((prev) => Math.max(0, prev - 1))} disabled={currentIndex === 0}
            className="bg-navy-800 text-cream-200 px-3 py-1 rounded-lg text-sm hover:bg-navy-700 disabled:opacity-30 transition">
            ← Forrige
          </button>
          <span className="text-navy-400 text-sm">{currentIndex + 1} / {presentationList.length}</span>
          <button onClick={() => setCurrentIndex((prev) => Math.min(presentationList.length - 1, prev + 1))} disabled={currentIndex === presentationList.length - 1}
            className="bg-navy-800 text-cream-200 px-3 py-1 rounded-lg text-sm hover:bg-navy-700 disabled:opacity-30 transition">
            Neste →
          </button>
        </div>
      </div>

      {showProgram && (
        <div className="absolute top-12 right-4 bg-navy-800 border border-navy-700 rounded-xl shadow-2xl p-4 z-10 w-80">
          <h3 className="font-bold text-cream-100 mb-3 text-sm uppercase tracking-wide">Program</h3>
          {presentationList.map((p, index) => (
            <div key={p.id} onClick={() => { setCurrentIndex(index); setShowProgram(false); }}
              className={`p-2 rounded-lg cursor-pointer mb-1 transition-colors ${index === currentIndex ? 'bg-navy-700' : 'hover:bg-navy-700'}`}>
              <p className="font-medium text-cream-100 text-sm">{getProgramIcon(p.type)} {p.title}</p>
              {p.type === 'presentation' && (
                <p className="text-xs text-navy-300">{p.speaker_name}{p.start_time && ` · ${p.start_time.slice(0, 5)}`}{p.end_time && ` - ${p.end_time.slice(0, 5)}`}</p>
              )}
              {(p.type === 'pause' || p.type === 'info') && p.start_time && (
                <p className="text-xs text-navy-300">{p.start_time.slice(0, 5)}{p.end_time && ` - ${p.end_time.slice(0, 5)}`}</p>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="flex-1 relative overflow-hidden">
        {!currentPresentation ? (
          <div className="flex items-center justify-center h-full bg-navy-900">
            <p className="text-navy-400">Ingen presentasjoner</p>
          </div>
        ) : currentPresentation.type === 'pause' ? (
          <PauseSlide presentation={currentPresentation} conference={conference} />
        ) : currentPresentation.type === 'info' ? (
          <InfoSlide presentation={currentPresentation} conference={conference} />
        ) : (
          <iframe key={currentPresentation.id} src={getFileUrl(currentPresentation)}
            className="w-full h-full border-0" title={currentPresentation.title} allowFullScreen />
        )}
      </div>

<div className="bg-navy-900 border-t border-navy-800 px-4 py-2">
  <div className="flex justify-between items-center">
    <div className="flex-1 text-center">
      <p className="text-cream-200 font-medium text-sm">
        {getProgramIcon(currentPresentation?.type)} {currentPresentation?.title}
        {currentPresentation?.type === 'presentation' && currentPresentation?.speaker_name && (
          <span className="text-navy-400 font-normal"> · {currentPresentation.speaker_name}</span>
        )}
        {currentPresentation?.type === 'presentation' && currentPresentation?.speaker_organization && (
          <span className="text-navy-400 font-normal"> · {currentPresentation.speaker_organization}</span>
        )}
      </p>
    </div>
    {presentationList[currentIndex + 1] && (
      <div className="text-right shrink-0 ml-8 border-l border-navy-700 pl-6">
        <p className="text-navy-500 text-xs uppercase tracking-wide">Neste</p>
        <p className="text-navy-300 text-sm font-medium">
          {getProgramIcon(presentationList[currentIndex + 1].type)} {presentationList[currentIndex + 1].title}
        </p>
        {presentationList[currentIndex + 1].type === 'presentation' && presentationList[currentIndex + 1].speaker_name && (
          <p className="text-navy-500 text-xs">{presentationList[currentIndex + 1].speaker_name}</p>
        )}
      </div>
    )}
  </div>
</div>
    </div>
  );
};

export default Present;
